import { useWalletDetails } from "@sundae/wallet-react";
import { AssetAmount, AssetRatio, IAssetAmountMetadata } from "@sundaeswap/asset";
import {
  Dispatch,
  FC,
  ReactNode,
  SetStateAction,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

import { useDebounce } from "@sundae/react-hooks";
import { QueryObserverResult, RefetchOptions, RefetchQueryFilters } from "@tanstack/react-query";
import { UTxO } from "lucid-cardano";
import { useParams } from "react-router-dom";
import { ADA_METADATA } from "../../constants/cardano.constants";
import { INPUT_DEBOUNCE_IN_MS } from "../../constants/page.constants";
import {
  Asset,
  Status,
  TasteTest,
  Transaction,
  TransactionType,
} from "../../gql/generated/taste-test.sdk";
import { useTasteTestBySlug, useTasteTestHistory } from "../../gql/hooks/taste-test.query";
import { useCalculateContributionOutcome } from "../../hooks/taste-test/useCalculateContributionOutcome";
import { usePersistedTasteTestContributionsHistory } from "../../hooks/taste-test/usePersistedTasteTestContributions";
import {
  IUseTasteTestPersonalization,
  useTasteTestPersonalization,
} from "../../hooks/taste-test/useTasteTestPersonalization";
import { useAdaBalance } from "../../hooks/useAdaBalance";
import { OrderActions, useOrderContext } from "../../stores/OrderContext";
import { IAssetMetaData } from "../../types/Asset.types";

export const TRENDING_TASTE_TEST_THRESHOLD = 100_000_000_000n; // Lovelaces --> 100k ADA

export interface IContributionOutcome {
  assetRatio: AssetRatio<Asset>;
  contributionValueAda: AssetAmount<IAssetAmountMetadata>;
  contributionValueToken: AssetAmount<IAssetAmountMetadata>;
  poolShare: number;
}

export type TActionType = "deposit" | "withdraw" | "update" | "claim";

export interface ITasteTestLocalStorageContributions {
  createdAt?: number; // Timestamp
  details?: {
    assetId?: string;
    amount?: string; // Formatted Amount
  };
  expiresAt?: number; // Timestamp
  id?: string;
  txHash?: string;
  type?: TActionType;
}

export interface ITasteTestDetailContext {
  derived: {
    contributionOutcome?: IContributionOutcome;
    depositAssetAmount?: AssetAmount<IAssetMetaData>;
    depositAssetAmountExceedsBalance?: boolean;
    hasContribution?: boolean;
    latestHistoryRecord?: Transaction;
  };
  handlers: {
    refetchTasteTest?: <TPageData>(
      options?: (RefetchOptions & RefetchQueryFilters<TPageData>) | undefined
    ) => Promise<QueryObserverResult<TasteTest, unknown>>;
    removePersistedContributionsHistory: () => void;
    resetTasteTestState: (removePersistedContributions?: boolean) => void;
    setActionType: Dispatch<SetStateAction<TActionType>>;
    setDepositValue: Dispatch<SetStateAction<string>>;
    setIsCountdownActive: Dispatch<SetStateAction<boolean>>;
    setRefetchTransactionHistory: Dispatch<SetStateAction<boolean>>;
    setReservedUtxos: Dispatch<SetStateAction<UTxO[] | undefined>>;
    setShowCurrentContribution: Dispatch<SetStateAction<boolean>>;
    updatePersistedContributionsHistory: Dispatch<
      SetStateAction<ITasteTestLocalStorageContributions[]>
    >;
  };
  state: {
    actionType: TActionType;
    depositValue?: string;
    debouncedDepositValue?: string;
    error?: unknown;
    isLoading?: boolean;
    persistedContributionsHistory?: ITasteTestLocalStorageContributions[];
    refetchTransactionHistory?: boolean;
    reservedUtxos?: UTxO[];
    showCurrentContribution?: boolean;
    tasteTest?: TasteTest;
    tasteTestHistory?: Transaction[];
    tasteTestPersonalization?: IUseTasteTestPersonalization;
    tasteTestStatus: {
      isTasteTestClosing: boolean;
      isTasteTestConcluded: boolean;
      isTasteTestDraft: boolean;
      isTasteTestGeofenced: boolean;
      isTasteTestInactive: boolean;
      isTasteTestModerated: boolean;
      isTasteTestPending: boolean;
      isTasteTestReadOnly: boolean;
    };
  };
}

const defaultTasteTestDetailState: ITasteTestDetailContext = {
  derived: {},
  handlers: {
    removePersistedContributionsHistory: () => {},
    resetTasteTestState: () => {},
    setActionType: () => {},
    setDepositValue: () => {},
    setRefetchTransactionHistory: () => {},
    setIsCountdownActive: () => {},
    setReservedUtxos: () => {},
    setShowCurrentContribution: () => {},
    updatePersistedContributionsHistory: () => {},
  },
  state: {
    actionType: "deposit",
    tasteTestStatus: {
      isTasteTestClosing: false,
      isTasteTestConcluded: false,
      isTasteTestDraft: false,
      isTasteTestGeofenced: false,
      isTasteTestInactive: false,
      isTasteTestModerated: false,
      isTasteTestPending: false,
      isTasteTestReadOnly: false,
    },
  },
};

const TasteTestDetailContext = createContext(defaultTasteTestDetailState);

export const useTasteTestContext = () => useContext(TasteTestDetailContext);

interface ITasteTestDetailContextProviderProps {
  children: ReactNode;
}

export const TasteTestDetailContextProvider: FC<ITasteTestDetailContextProviderProps> = ({
  children,
}) => {
  const {
    details: { stakingAddress },
  } = useWalletDetails();
  const { slug: tasteTestSlug = "" } = useParams();
  const {
    dispatch,
    state: {
      flowData: { flowState },
    },
  } = useOrderContext();
  const { availableAdaBalance } = useAdaBalance();

  /**
   * Persisted Information to render during the Pending Tx state
   */
  const {
    persistedContributionsHistory,
    removePersistedContributionsHistory,
    updatePersistedContributionsHistory,
  } = usePersistedTasteTestContributionsHistory();

  /**
   * Local States accounting for the `TasteTest` flow
   */
  const [actionType, setActionType] = useState<TActionType>("deposit");
  const [depositValue, setDepositValue] = useState("");
  const [refetchTransactionHistory, setRefetchTransactionHistory] = useState(false);
  const [showCurrentContribution, setShowCurrentContribution] = useState(true);
  const [reservedUtxos, setReservedUtxos] = useState<UTxO[]>();
  const [contributionOutcome, setContributionOutcome] = useState<IContributionOutcome>();
  const debouncedDepositValue = useDebounce(depositValue, { wait: INPUT_DEBOUNCE_IN_MS });

  /**
   * Flag to determine if the countdown is active.
   * This is used to avoid pending updates from our API that might not be in immediate sync with the countdown (TasteTestStatus).
   */
  const [isCountdownActive, setIsCountdownActive] = useState(true);

  /**
   * Data Fetching
   */
  const { data: tasteTest, error, isLoading, refetch } = useTasteTestBySlug(tasteTestSlug);
  const { isLoading: isLoadingTasteTestPersonalization, tasteTestPersonalization } =
    useTasteTestPersonalization(tasteTest);
  const { data: tasteTestHistory, isLoading: isLoadingTasteTestHistory } = useTasteTestHistory(
    stakingAddress,
    tasteTest?.policyId
  );
  const latestHistoryRecord = tasteTestHistory
    ?.filter(({ type }) =>
      [TransactionType.Update, TransactionType.Deposit, TransactionType.Withdraw].includes(type)
    )
    ?.pop();
  const { calculateContributionOutcome, calculateShareOfPool } = useCalculateContributionOutcome({
    latestHistoryRecord,
    currentBalance: tasteTest?.currentBalance,
  });

  /**
   * Taste Test States
   */
  const isTasteTestInactive =
    [Status.Concluded, Status.Closing].includes(tasteTest?.status ?? ("" as Status)) ||
    !isCountdownActive;
  const isTasteTestClosing = tasteTest?.status === Status.Closing;
  const isTasteTestConcluded = tasteTest?.status === Status.Concluded;
  const isTasteTestDraft = tasteTest?.status === Status.Draft;
  const isTasteTestGeofenced = tasteTest?.status === Status.Geofenced;
  const isTasteTestModerated = tasteTest?.status === Status.Moderated;
  const isTasteTestPending = tasteTest?.status === Status.Pending;
  const isTasteTestReadOnly = [
    Status.Draft,
    Status.Pending,
    Status.Geofenced,
    Status.Moderated,
  ].includes(tasteTest?.status ?? ("" as Status));

  /**
   * Transform the `depositValue` into an `AssetAmount` instance
   */
  const depositAssetAmount = useMemo(
    () => AssetAmount.fromValue(depositValue, ADA_METADATA),
    [depositValue]
  );
  const depositAssetAmountExceedsBalance = useMemo(() => {
    return depositAssetAmount.value.greaterThan(availableAdaBalance?.quantity.value);
  }, [depositAssetAmount, availableAdaBalance]);

  // We're taking the last transaction from the history, which is the one that has the final accumulative values
  const amountDeposited = useMemo(
    () =>
      new AssetAmount(
        latestHistoryRecord?.depositCumulative[0].amount,
        latestHistoryRecord?.depositCumulative[0].asset as unknown as IAssetMetaData
      ),
    [latestHistoryRecord?.depositCumulative]
  );

  /**
   * Flag to determine if the user has contributed to the `TasteTest`
   */
  const hasContribution =
    !!(latestHistoryRecord && latestHistoryRecord.depositCumulative[0].amount !== "0") &&
    !!amountDeposited;

  /**
   * Reset the order flow state and remove the persisted contributions history.
   * @returns {void}
   */
  const resetTasteTestState = useCallback(
    (clearPersistentHistory?: boolean) => {
      OrderActions.resetOrderFlow(dispatch, "TasteTestOrderView.orderSuccess.onAction");
      setShowCurrentContribution(true);
      setActionType("deposit");
      setDepositValue("");
      OrderActions.setOrderConsent({ checked: false }, dispatch);

      if (clearPersistentHistory) {
        removePersistedContributionsHistory();
      }
    },
    [
      dispatch,
      flowState,
      removePersistedContributionsHistory,
      setActionType,
      setShowCurrentContribution,
    ]
  );

  /**
   * Effect to calculate the contribution outcome
   */
  useEffect(() => {
    if (!tasteTest || !tasteTest?.currentBalance || !depositAssetAmount) {
      return;
    }

    if (!tasteTest.currentBalance || depositAssetAmount.amount === 0n) {
      setContributionOutcome(undefined);
      return;
    }

    let userDepositCumulative = depositAssetAmount;

    if (latestHistoryRecord) {
      const latestHistoryRecordAmount = new AssetAmount(
        latestHistoryRecord.depositCumulative[0].amount,
        ADA_METADATA
      );
      userDepositCumulative = userDepositCumulative.add(latestHistoryRecordAmount);
    }

    /*const amountLpTokens = tokenSuppliedAmount.value
      .multiply(userDepositCumulative.value)
      .divide(tokenGatheredAmount.add(userDepositCumulative).value)
      .toNumber();*/

    const poolShare = calculateShareOfPool(userDepositCumulative);
    const { assetRatio, contributionValueAdaAssetAmount, contributionValueTokenAssetAmount } =
      calculateContributionOutcome(userDepositCumulative);

    const contributionOutcome: IContributionOutcome = {
      poolShare,
      assetRatio,
      contributionValueAda: contributionValueAdaAssetAmount,
      contributionValueToken: contributionValueTokenAssetAmount,
    };

    setContributionOutcome(contributionOutcome);
  }, [
    tasteTest,
    depositAssetAmount,
    latestHistoryRecord,
    calculateContributionOutcome,
    calculateShareOfPool,
  ]);

  return (
    <TasteTestDetailContext.Provider
      value={{
        derived: {
          contributionOutcome,
          depositAssetAmount,
          depositAssetAmountExceedsBalance,
          hasContribution,
          latestHistoryRecord,
        },
        handlers: {
          refetchTasteTest: refetch,
          removePersistedContributionsHistory,
          resetTasteTestState,
          setActionType,
          setDepositValue,
          setIsCountdownActive,
          setRefetchTransactionHistory,
          setReservedUtxos,
          setShowCurrentContribution,
          updatePersistedContributionsHistory,
        },
        state: {
          actionType,
          depositValue,
          debouncedDepositValue,
          error,
          isLoading: isLoading || isLoadingTasteTestPersonalization || isLoadingTasteTestHistory,
          persistedContributionsHistory,
          refetchTransactionHistory,
          reservedUtxos,
          showCurrentContribution,
          tasteTest,
          tasteTestHistory,
          tasteTestPersonalization,
          tasteTestStatus: {
            isTasteTestClosing,
            isTasteTestConcluded,
            isTasteTestDraft,
            isTasteTestGeofenced,
            isTasteTestInactive,
            isTasteTestModerated,
            isTasteTestPending,
            isTasteTestReadOnly,
          },
        },
      }}
    >
      {children}
    </TasteTestDetailContext.Provider>
  );
};
